import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Button/Button";
import LeftPanel from "../../components/LeftPanel/LeftPanel";
import OnboardingStepHeader from "../../components/OnboardingStepHeader/OnboardingStepHeader";
import { completeOnboarding, closeModal } from "../../redux/slices/OfficeOnboardingSlice";
import { getOffice } from "../../redux/slices/OfficeSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { logger } from "../../utils/logger";

const log = logger.getLogger("OnboardingStep5");

const Step5: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isCompleted } = useSelector((state: RootState) => state.officeOnboarding);
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const { currentOffice } = useSelector((state: RootState) => state.office);
  const hasMarkedComplete = useRef(false);

  // Mark onboarding as complete when Step 5 is rendered
  // This ensures onboarding is complete even if user navigates away
  useEffect(() => {
    if (!isCompleted && !hasMarkedComplete.current) {
      hasMarkedComplete.current = true;
      const officeId = currentUser?.offices?.[0]?.officeId;
      dispatch(completeOnboarding(officeId));
    }
  }, [dispatch, isCompleted, currentUser]);

  const handleVideoPlay = () => {
    log.debug("Video play clicked");
  };

  const handleCreateServiceRequest = async () => {
    try {
      // Get office ID from currentOffice
      const officeId = currentOffice?.officeId;

      if (!officeId) {
        log.error("Office ID not found in currentOffice");
        return;
      }

      // Trigger API call to fetch office data
      await dispatch(getOffice({ officeId })).unwrap();

      // Mark onboarding as complete and close the modal
      await dispatch(completeOnboarding(officeId)).unwrap();
      dispatch(closeModal());

      // Navigate to create request page
      navigate(`/offices/${officeId}/requests/create-request`);
    } catch (err) {
      log.error(`Failed to complete onboarding: ${JSON.stringify(err)}`);
    }
  };

  const handleStartTour = () => {
    const tourUrl = process.env.REACT_APP_OFFICES_TOUR_URL; // "?__ug__=159551"

    const finalUrl = `${window.location.origin}${window.location.pathname}${tourUrl}`;

    window.location.href = finalUrl;
  };

  const handleViewResources = () => {
    // TODO: Navigate to resources page
    window.open("https://help.docnote.com/en", "_blank");
  };

  const handleBookMeeting = () => {
    window.open(
      "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2fiJ-13Z9uT9RRFKIxu9bDmFfshL8bZTcRcpDf6j4526wZSLW_Cj9J8E2a_ncK9T8u2EKBocb1",
      "_blank"
    );
    log.debug("Book meeting clicked");
  };

  return (
    <div className="flex h-full min-h-0">
      {/* Left Panel */}
      <LeftPanel onVideoPlay={handleVideoPlay} />

      {/* Right Panel */}
      <div className="w-full md:w-2/3 bg-white flex flex-col h-full min-h-0">
        {/* Sticky Header with Progress */}
        <div className="sticky top-0 z-10 bg-white flex-shrink-0">
          <OnboardingStepHeader currentStep={5} totalSteps={5} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex justify-center">
            <div className="p-8 w-full max-w-4xl">
              {/* Completion Section */}
              <div className="mb-14 space-y-2.5 text-center">
                <div className="flex justify-center">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M72.6698 33.3333C74.1922 40.8043 73.1072 48.5714 69.596 55.3393C66.0848 62.1072 60.3595 67.4668 53.3749 70.5244C46.3903 73.5819 38.5686 74.1526 31.2142 72.1412C23.8598 70.1298 17.4171 65.658 12.9608 59.4714C8.50438 53.2848 6.30358 45.7574 6.72539 38.1446C7.1472 30.5317 10.1661 23.2936 15.2787 17.6372C20.3913 11.9808 27.2884 8.24807 34.8201 7.06148C42.3517 5.87489 50.0625 7.30617 56.6665 11.1166"
                      stroke="#D4F14E"
                      strokeWidth="6.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M30 36.6666L40 46.6666L73.3333 13.3333"
                      stroke="#D4F14E"
                      strokeWidth="6.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h1 className="font-inter tracking-[-0.31px] text-[22px]">Your Office Setup is Complete</h1>
                <p className="text-lg tracking-[-0.44px]">You&apos;re now ready to create your first Service request and invite patients.</p>
                <Button
                  text="Create Service Request →"
                  onClick={handleCreateServiceRequest}
                  variant="Primary"
                  width="fit"
                  className="px-8 py-3 !text-[16px]"
                />
              </div>

              {/* Action Cards */}
              <div className="space-y-4">
                {/* Take a Tour Card */}
                <div className="border border-gray-200 rounded-[10px] px-6 py-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base font-normal font-inter tracking-[-0.31px]">Take a Tour of Docnote</h3>
                        <p className="text-[#4A5565] text-sm tracking-[-0.15px]">Walk through the platform features step by step</p>
                      </div>
                    </div>
                    <button
                      onClick={handleStartTour}
                      className="px-5 py-2 border border-gray-300 rounded w-40 text-sm font-medium font-inter text-black hover:bg-gray-50 transition-colors"
                    >
                      Start Tour
                    </button>
                  </div>
                </div>

                {/* Explore Resources Card */}
                <div className="border border-gray-200 rounded-[10px] px-6 py-4 hover:shadow-md transition-shadow hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base font-normal font-inter tracking-[-0.31px]">Explore Self-Service Resources</h3>
                        <p className="text-[#4A5565] text-sm tracking-[-0.15px]">Guides, FAQs, and best practices at your fingertips</p>
                      </div>
                    </div>
                    <button
                      onClick={handleViewResources}
                      className="px-5 py-2 border border-gray-300 rounded w-40 text-sm font-medium font-inter text-black hover:bg-gray-50 transition-colors"
                    >
                      View Resources
                    </button>
                  </div>
                </div>

                {/* Meet Specialist Card */}
                <div className="border border-gray-200 rounded-[10px] px-6 py-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base font-normal font-inter tracking-[-0.31px]">Meet Your Customer Success Specialist</h3>
                        <p className="text-[#4A5565] text-sm tracking-[-0.15px]">Schedule time with our team for tailored support</p>
                      </div>
                    </div>
                    <button
                      onClick={handleBookMeeting}
                      className="px-5 py-2 border border-gray-300 rounded w-40 text-sm font-medium font-inter text-black hover:bg-gray-50 transition-colors"
                    >
                      Book a Meeting
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step5;
